import React, { useState, useEffect, useRef } from "react";
import "./Counter.css";
import CustomModal from "../CustomModal";
import useAuth from "../../context/AuthContext";

// Horário fixo de desligamento (20:00 - pode ser alterado conforme necessário)
const getShutdownTime = () => {
  const now = new Date();
  const shutdown = new Date();
  
  // Definir horário de desligamento: 20:00:00 (horário local)
  shutdown.setHours(20, 0, 0, 0);
  
  // Se já passou das 20h hoje, usar 20h de amanhã
  if (now >= shutdown) {
    shutdown.setDate(shutdown.getDate() + 1);
    shutdown.setHours(20, 0, 0, 0); // Garantir que seja 20:00 do próximo dia
  }
  
  return shutdown;
};

const Counter = () => {
  const { signOut } = useAuth();
  const [shutdownTime] = useState(() => getShutdownTime());
  const [timeLeft, setTimeLeft] = useState(null);
  const [displayTime, setDisplayTime] = useState({ hours: 0, minutes: 0, seconds: 0 });
  const [isActive, setIsActive] = useState(false);
  const [shouldShow, setShouldShow] = useState(false);
  const lastWarningMinuteRef = useRef(null);
  const intervalRef = useRef(null);

  const showWarningModal = React.useCallback((minutes) => {
    const messages = {
      15: "A aplicação será desligada em 15 minutos. Por favor, salve seu trabalho.",
      10: "A aplicação será desligada em 10 minutos. Por favor, salve seu trabalho.",
      5: "A aplicação será desligada em 5 minutos. Por favor, salve seu trabalho.",
      2: "A aplicação será desligada em 2 minutos. Por favor, salve seu trabalho.",
      1: "A aplicação será desligada em 1 minuto. Por favor, salve seu trabalho imediatamente.",
    };

    CustomModal.show({
      title: "⚠️ Aviso de Desligamento",
      body: (
        <div style={{ textAlign: "center", padding: "20px" }}>
          <h4 style={{ color: "#dc3545", marginBottom: "15px" }}>
            {messages[minutes]}
          </h4>
          <p style={{ color: "#6c757d", fontSize: "14px" }}>
            A aplicação será desligada automaticamente às 20:00.
          </p>
        </div>
      ),
      footer: (
        <button
          className="btn btn-primary"
          onClick={() => CustomModal.hide()}
        >
          Entendi
        </button>
      ),
      size: "md",
    });
  }, []);

  // Lógica de contagem regressiva
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const diff = shutdownTime.getTime() - now.getTime();

      if (diff <= 0) {
        setTimeLeft(0);
        setIsActive(false);
        setShouldShow(false);
        // Deslogar usuário quando o tempo acabar
        if (signOut) {
          console.log('[Counter] Tempo de desligamento chegou ao fim, deslogando usuário...');
          setTimeout(() => {
            signOut();
          }, 1000); // Aguardar 1 segundo antes de deslogar
        }
        return;
      }

      setTimeLeft(diff);
      setIsActive(true);

      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setDisplayTime({ hours, minutes, seconds });

      // Verificar avisos - mostra quando o minuto exato é atingido
      const totalMinutes = Math.floor(diff / (1000 * 60));
      
      // Só mostrar o contador quando faltar 15 minutos ou menos
      setShouldShow(totalMinutes <= 14);
      
      const warningMinutes = [15, 10, 5, 2, 1];

      // Mostra aviso quando o minuto exato é atingido e ainda não foi mostrado
      if (warningMinutes.includes(totalMinutes) && lastWarningMinuteRef.current !== totalMinutes && seconds === 0) {
        lastWarningMinuteRef.current = totalMinutes;
        showWarningModal(totalMinutes);
      }
    };

    calculateTimeLeft();
    intervalRef.current = setInterval(calculateTimeLeft, 1000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [shutdownTime, showWarningModal, signOut]);

  // Se o tempo acabou, mostrar mensagem de desligamento
  if (timeLeft === 0) {
    return (
      <div className="counter-container">
        <div className="counter-expired">
          <h3>⚠️ Aplicação sendo desligada</h3>
        </div>
      </div>
    );
  }

  // Só mostrar contador quando faltar 15 minutos ou menos
  if (!isActive || !shouldShow) {
    return null;
  }

  return (
    <div className="counter-container">
      <div className="counter-wrapper">
        <div className="counter-label">Desligamento em:</div>
        <div className="counter-display">
          <div className="counter-unit">
            <div className="counter-value">{String(displayTime.hours).padStart(2, "0")}</div>
            <div className="counter-label-unit">Horas</div>
          </div>
          <div className="counter-separator">:</div>
          <div className="counter-unit">
            <div className="counter-value">{String(displayTime.minutes).padStart(2, "0")}</div>
            <div className="counter-label-unit">Minutos</div>
          </div>
          <div className="counter-separator">:</div>
          <div className="counter-unit">
            <div className="counter-value">{String(displayTime.seconds).padStart(2, "0")}</div>
            <div className="counter-label-unit">Segundos</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Counter;
